import { Box, CircularProgress, useMediaQuery } from "@material-ui/core";
import { ReactComponent as QuestionIcon } from '../../assets/icons/question.svg';
import { Skeleton } from "@mui/material";
import { loadStripe } from "@stripe/stripe-js";
import { ReactComponent as PlusIcon } from "assets/icons/plus.svg";
import DeleteSvgComponent from "assets/icons/svg/DeleteSvg/Delete";
import { MyCheckbox } from "components";
import AddAchCard from "components/addAchCard/AddAchCard";
import Button from "components/button/Button";
import AlertDialog from "components/Dialog";
import MyModal from "components/MyModal";
import VerifyAchCardInfo from "components/verifyAchCardInfo/VerifyAchCardInfo";
import { checkIsStripeByPaymentGateway } from "helper/checkIsStripe";
import FinanceService from "Lib/API/Finance";
import React, { useEffect, useMemo, useState } from "react";
import { toastr } from "react-redux-toastr";
import { getAccessTokenSilently, getLocalStorageItem } from "utils/functions";
import {
  CreditCardMethodButton,
  CreditCardMethodContainer,
  CreditCardMethodContent,
  CreditCardMethodHeader,
  CreditCardMethodItem,
  CreditCardMethodItemVerify,
  CreditCardMethodItemVerifyInput,
  CreditCardMethodList,
  CreditCardMethodTitle,
  SMVerifyInput,
} from "./Theme";
import { Fonts, FontColors } from "theme/fontConfig";

const AchMethod = ({ bottomAdd = false }) => {
  const accountId = getLocalStorageItem("accountID");
  const email = getLocalStorageItem("accountInfo", true)?.primary_email_address;
  const firstName = getLocalStorageItem("accountInfo", true)?.firstName;
  const lastName = getLocalStorageItem("accountInfo", true)?.lastName;
  const enableAchPay = getLocalStorageItem("paymentMethodAch", true);
  const [stripePromise, setStripePromise] = useState(null);

  useEffect(() => {
    const key = getLocalStorageItem("paymentMethodAch", true)?.paymentGateway
      ?.keys?.publicKey;
    if (key) {
      setStripePromise(loadStripe(key));
    }
  }, []);

  const [cardList, setCardList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [creditId, setCreditId] = useState(null);
  const [openAlert, setOpenAlert] = useState(false);
  const [openModal, setOpenModal] = useState(false);
  const [defaultCard, setDefaultCard] = useState(null);
  const [setDefaultLoading, setSetDefaultLoading] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [openVerifyModal, setOpenVerifyModal] = useState(false);
  const [verifyError, setVerifyError] = useState(null);
  const [achSetupLoader, setAchSetupLoader] = useState(false);
  const [verifyData, setVerifyData] = useState({
    achNumberOne: "",
    achNumberTwo: "",
    achSM: "",
  });
  const [verifyLoading, setVerifyLoading] = useState(false);
  const [instructionsModal, setInstructionsModal] = useState(false);

  const isSmallScreen = useMediaQuery("(max-width:750px)");

  const isStripe = useMemo(
    () => checkIsStripeByPaymentGateway(enableAchPay?.paymentGateway),
    [enableAchPay]
  );

  const getAchCards = async () => {
    setLoading(true);
    const token = localStorage.getItem("token");
    await FinanceService.getACHCardsByAccountId(token, accountId)
      .then((res) => {
        setCardList(res?.ach?.data || []);
        const findDefaultCard = res?.ach?.data?.find(
          (card) => card?.is_default
        );
        if (findDefaultCard) {
          setDefaultCard(findDefaultCard?.id);
        } else if (res?.ach?.data?.length === 1) {
          handleDefaultCardChange(res?.ach?.data[0]?.id);
        }
      })
      .catch((err) => {
        toastr.error("Error", err?.data?.message);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const handleSetDefaultCard = async (cardId) => {
    const token = await getAccessTokenSilently();
    const data = {
      accountId,
      achId: cardId,
    };
    setSetDefaultLoading(true);
    await FinanceService.setDefaultAch(data, token)
      .then((res) => {
        toastr.success("Success", "Default card set successfully");
      })
      .catch((err) => {
        toastr.error("Error", err?.data?.message || "Something went wrong");
      })
      .finally(() => {
        const timeOut = setTimeout(() => {
          setSetDefaultLoading(false);
        }, 2000);
        return () => {
          clearTimeout(timeOut);
        };
      });
  };

  const handleDelete = async (cardID) => {
    setDeleteLoading(true);
    const token = await getAccessTokenSilently();
    await FinanceService.deleteAchCard(token, cardID, accountId)
      .then(() => {
        toastr.success("Success", "ACH Deleted Successfully");
        getAchCards();
        closeConfirmDialog();
      })
      .catch((error) => {
        toastr.error("Error", error?.data?.message || "Something went wrong");
      })
      .finally(() => {
        setDeleteLoading(false);
      });
  };

  const handleVerifyAch = async (item) => {
    setVerifyLoading(true);
    let data = {
      accountId,
      achId: item?.id,
      firstAmount: verifyData?.achNumberOne || null,
      secondAmount: verifyData?.achNumberTwo || null,
      smCode: verifyData?.achSM || null,
    };

    if (data.firstAmount === null) delete data.firstAmount;
    if (data.secondAmount === null) delete data.secondAmount;
    if (data.smCode === null) delete data.smCode;
    const token = await getAccessTokenSilently();
    FinanceService.verifyAch(data, token)
      .then(() => {
        getAchCards();
        toastr.success("Success", "ACH account verified successfully");
      })
      .catch((err) => {
        setVerifyError(err?.data?.message);
        toastr.error(
          "Error",
          err?.data?.message ?? "Please enter valid details"
        );
        setTimeout(() => {
          setVerifyError(null);
        }, 7000);
      })
      .finally(() => {
        setVerifyLoading(false);
      });
  };

  useEffect(() => {
    getAchCards();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onVerifyAchChange = (e) => {
    const { value, name } = e.target;
    setVerifyData((prevData) => ({
      ...prevData,
      [name]: value,
    }));
  };

  const handleDefaultCardChange = (cardId) => {
    setDefaultCard(cardId);
    handleSetDefaultCard(cardId);
  };

  const openConfirmDialog = (id) => {
    setCreditId(id);
    setOpenAlert(true);
  };

  const closeConfirmDialog = () => {
    setOpenAlert(false);
    setCreditId(null);
  };

  const openVerifyInfoModal = () => setOpenVerifyModal(true);
  const closeVerifyInfoModal = () => setOpenVerifyModal(false);

  const isVerifyBtnDisabled = useMemo(() => {
    return verifyLoading || !(
      (verifyData?.achNumberOne && verifyData?.achNumberTwo && !verifyData?.achSM) ||
      (!verifyData?.achNumberOne && !verifyData?.achNumberTwo && verifyData?.achSM)
    );
  }, [verifyData, verifyLoading]);

  const handleCheckStripeSetup = async () => {
    if (!isStripe) {
      setOpenModal(true);
      return;
    }

    if (!stripePromise) {
      toastr.error("Stripe is still loading. Please try again.");
      return;
    }

    setAchSetupLoader(true);
    const token = await getAccessTokenSilently();
    const stripe = await stripePromise;
    await FinanceService.checkStripeSetup(token)
      .then(async (res) => {
        const { error: collectError } = await stripe.collectBankAccountForSetup(
          {
            clientSecret: res?.paymentIntent?.client_secret,
            params: {
              payment_method_type: "us_bank_account",
              payment_method_data: {
                billing_details: {
                  name: firstName + " " + lastName,
                  email: email || undefined,
                },
              },
            },
          }
        );
        if (!collectError) {
          const {
            setupIntent,
            error: confirmError,
          } = await stripe.confirmUsBankAccountSetup(
            res?.paymentIntent?.client_secret
          );
          if (setupIntent) {
            await FinanceService.addACHAccount(token, accountId, setupIntent)
              .then((res) => {
                toastr.success("Success", "ACH Added Successfully");
              })
              .catch((err) => {
                toastr.error(
                  "Error",
                  err?.data?.message || "Something went wrong"
                );
              });
          }

          if (confirmError) {
            toastr.error(
              "Error",
              confirmError.message || "Something went wrong during confirmation"
            );
          }
        }
      })
      .catch((err) => {
        toastr.error("Error", err?.data?.message || "Something went wrong");
      });
    setAchSetupLoader(false);
  };

  return (
    <>
      <CreditCardMethodContainer>
        {!bottomAdd && (
          <CreditCardMethodHeader>
            <CreditCardMethodTitle>ACH</CreditCardMethodTitle>
            <Button
              variant="primary"
              size="md"
              onClick={handleCheckStripeSetup}
              style={{
                minWidth: 165,
              }}
              disabled={achSetupLoader}
            >
              {achSetupLoader ? (
                <CircularProgress size={18} style={{ color: FontColors.White }} />
              ) : (
                <PlusIcon />
              )}
              <span>Add New ACH</span>
            </Button>
          </CreditCardMethodHeader>
        )}

        <CreditCardMethodContent>
          {loading ? (
            <>
              {[1, 2, 3, 4].map((i) => (
                <Box
                  key={i}
                  sx={{
                    width: "100%",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    p: bottomAdd ? 0 : 1,
                    borderRadius: 2,
                    bgcolor: "background.paper",
                    boxShadow: 1,
                    mb: 2,
                  }}
                >
                  {/* LEFT SIDE */}
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 2,
                      width: "100%",
                    }}
                  >
                    {/* Card brand / Icon placeholder */}
                    <Skeleton
                      variant="rectangular"
                      width={60}
                      height={40}
                      sx={{ borderRadius: 1 }}
                    />

                    {/* Card information */}
                    <Box sx={{ flex: 1 }}>
                      <Skeleton
                        variant="text"
                        height={22}
                        sx={{ width: "70%" }}
                      />
                      <Skeleton
                        variant="text"
                        height={18}
                        sx={{ width: "45%", mt: 0.5 }}
                      />
                    </Box>
                  </Box>
                </Box>
              ))}
            </>
          ) : cardList?.length > 0 ? (
            cardList?.map((card) => (
              <CreditCardMethodList key={card?.id} className="credit-card">
                <CreditCardMethodItem
                  bottomAdd={bottomAdd}
                  isSmallScreen={isSmallScreen}
                >
                  <p>
                    {`${card.nameOnAccount ? card.nameOnAccount : ""} ${card.accountNumber ? "/ " + card.accountNumber : ""
                      }`}
                    <CreditCardMethodButton
                      onClick={() => openConfirmDialog(card?.id)}
                    >
                      <DeleteSvgComponent color={"gray"} />
                    </CreditCardMethodButton>
                  </p>
                  <div
                    style={
                      setDefaultLoading
                        ? {
                          pointerEvents: "none",
                          opacity: 0.7,
                        }
                        : {}
                    }
                  >
                    <MyCheckbox
                      title={"Set as default"}
                      handlechange={() => {
                        if (card?.id !== defaultCard) {
                          handleDefaultCardChange(card?.id);
                        }
                      }}
                      checked={defaultCard === card?.id}
                    />
                  </div>

                  {card?.status !== "verified" && (
                    <>
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "8px",
                          width: bottomAdd && isSmallScreen ? "100%" : "auto",
                          justifyContent: "flex-end",
                        }}
                      >
                        <span
                          style={{
                            marginLeft: 8,
                            cursor: "pointer",
                            fontWeight: Fonts.GeneralHeaderFont.fontWeight,
                            fontSize: Fonts.GeneralHeaderFont.fontSize,
                            borderRadius: "50%",
                            border: "1px solid #ccc",
                            width: 24,
                            height: 24,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            background: "#f5f5f5",
                          }}
                          title="How to verify?"
                          onClick={openVerifyInfoModal}
                        >
                          ?
                        </span>
                      </div>
                      <CreditCardMethodItemVerify>
                        {/* <QuestionIcon width={20} style={{ marginTop: "0.5rem", marginRight: "0.5rem", cursor: "pointer" }} onClick={() => setInstructionsModal(true)} /> */}

                        <CreditCardMethodItemVerifyInput
                          type="text"
                          maxLength={2}
                          name="achNumberOne"
                          onChange={onVerifyAchChange}
                        />
                        <CreditCardMethodItemVerifyInput
                          type="text"
                          maxLength={2}
                          name="achNumberTwo"
                          onChange={onVerifyAchChange}
                        />
                        <p>OR</p>
                        <SMVerifyInput
                          type="text"
                          maxLength={6}
                          minLength={6}
                          name="achSM"
                          onChange={onVerifyAchChange}
                        />
                        <Button
                          size="sm"
                          variant="primary"
                          onClick={() => handleVerifyAch(card)}
                          disabled={isVerifyBtnDisabled}
                          style={{
                            minWidth: 70,
                          }}
                        >
                          {verifyLoading ? (
                            <CircularProgress color="#FFFFFF" size={16} />
                          ) : (
                            "Verify"
                          )}
                        </Button>
                      </CreditCardMethodItemVerify>
                      <Box>
                        <span style={{ fontSize: Fonts.GeneralBodyFont.fontSize, color: FontColors.Red }}>
                          {verifyError}
                        </span>
                      </Box>
                    </>
                  )}
                </CreditCardMethodItem>
              </CreditCardMethodList>
            ))
          ) : (
            <h1 className="no-cards" style={{ fontSize: Fonts.GeneralHeaderFont.fontSize }}>
              NO ACH
            </h1>
          )}
        </CreditCardMethodContent>
        {!loading && bottomAdd && (
          <Box
            sx={{
              display: "flex",
              width: "100%",
              justifyContent: isSmallScreen ? "flex-start" : "flex-end",
              alignItems: "flex-start",
            }}
          >
            <Button
              variant="primary"
              size="md"
              onClick={handleCheckStripeSetup}
              style={{
                minWidth: 165,
              }}
            >
              <PlusIcon />
              <span>Add New ACH</span>
            </Button>
          </Box>
        )}
      </CreditCardMethodContainer>

      <AlertDialog
        open={openAlert}
        handleClose={() => {
          closeConfirmDialog();
        }}
        onDecline={() => {
          setOpenAlert(false);
        }}
        onAccept={() => {
          handleDelete(creditId);
        }}
        content={"Are you sure you want to delete this ACH?"}
        contentStyle={{
          fontSize: Fonts.GeneralXLargeBodyFont.fontSize,
        }}
        disabled={deleteLoading}
      />
      <MyModal
        className="add-card-modal"
        open={openModal}
        onClose={() => setOpenModal(false)}
      >
        <AddAchCard
          setOpenModal={setOpenModal}
          refetchAchCards={getAchCards}
          closeModal={() => setOpenModal(false)}
          openVerifyInfoModal={openVerifyInfoModal}
          bottomAdd={bottomAdd}
        />
      </MyModal>
      <MyModal
        className="main__alert-dialog"
        open={openVerifyModal}
        onClose={closeVerifyInfoModal}
      >
        <VerifyAchCardInfo closeModal={closeVerifyInfoModal} />
      </MyModal>
    </>
  );
};

export default AchMethod;